import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Ticket, Check } from "lucide-react";
import { StripeEmbeddedCheckout } from "@/components/StripeEmbeddedCheckout";
import { getStripeEnvironment } from "@/lib/stripe";

const EarlyBirdOrderForm = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    quantity: "1",
  });

  const qty = Math.max(1, Math.min(10, parseInt(form.quantity || "1", 10) || 1));
  const amountDue = qty * 500;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const fullName = form.full_name.trim();
    const email = form.email.trim();

    if (!fullName || !email) {
      toast.error("Please fill out all required fields.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      const { data: checkoutData, error: checkoutErr } = await supabase.functions.invoke(
        "create-popup-checkout",
        {
          body: {
            kind: "early_bird",
            quantity: qty,
            email,
            fullName,
            environment: getStripeEnvironment(),
          },
        },
      );
      if (checkoutErr) throw checkoutErr;
      if (checkoutData?.error) {
        toast.error(checkoutData.error);
        setLoading(false);
        return;
      }
      if (checkoutData?.clientSecret) {
        setClientSecret(checkoutData.clientSecret);
        setSubmitted(true);
      } else {
        throw new Error("No checkout session returned");
      }
    } catch (err) {
      console.error("Early bird checkout error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-secondary/40 bg-secondary/10 p-6 text-center space-y-3">
          <div className="mx-auto w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center">
            <Check className="w-6 h-6 text-secondary-foreground" />
          </div>
          <h3 className="font-heading text-xl font-bold text-foreground">Complete Your Payment</h3>
          <p className="text-sm text-muted-foreground">
            Complete payment below. Your Early Bird ticket will be emailed to{" "}
            <strong>{form.email}</strong> once payment is complete.
          </p>
        </div>
        {clientSecret && <StripeEmbeddedCheckout clientSecret={clientSecret} />}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="rounded-md bg-primary/5 border border-primary/20 p-4">
        <div className="flex items-start gap-3">
          <Ticket className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <div className="text-sm text-foreground">
            <p className="font-semibold">Early Bird Ticket — $5 per person</p>
            <p className="text-muted-foreground mt-1">
              Enter the doors <strong>one hour before</strong> general admission. General
              admission opens one hour later and is <strong>free</strong>.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="eb_full_name">Full Name *</Label>
          <Input
            id="eb_full_name"
            required
            maxLength={100}
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="eb_email">Email Address *</Label>
          <Input
            id="eb_email"
            type="email"
            required
            maxLength={255}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="eb_qty">Quantity of Tickets *</Label>
          <Input
            id="eb_qty"
            type="number"
            required
            min={1}
            max={10}
            value={form.quantity}
            onChange={(e) => setForm({ ...form, quantity: e.target.value })}
          />
        </div>
      </div>

      <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4 text-sm text-yellow-900">
        Your Early Bird Ticket will be emailed to the address entered above after payment. If
        you do not see it in your inbox within the next few days, please check your spam or
        junk folder.
      </div>

      <div className="rounded-md bg-secondary/10 border border-secondary/30 p-3 text-sm text-foreground text-center">
        Amount due: <strong>${(amountDue / 100).toFixed(0)}</strong> ({qty} ticket
        {qty > 1 ? "s" : ""} × $5)
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full font-body font-bold text-base h-12"
      >
        {loading ? "Loading payment…" : `Continue to Payment — $${(amountDue / 100).toFixed(0)}`}
      </Button>
      <p className="text-xs text-muted-foreground text-center">
        Secure payment powered by Stripe.
      </p>
    </form>
  );
};

export default EarlyBirdOrderForm;
